import { Alignment, AppBar, Displaying, IconButton, typography, Typography } from "@bonusx-global/bx-design-system";
import { TimesSolid } from "@bonusx-global/bx-icons";
import Box from "@mui/material/Box";
import { useAtomValue } from "jotai";
import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useIsMobile from "../../../hooks/device/useIsMobile";
import { useBackNavigation } from "../../../hooks/layout/use-back-navigation/useBackNavigation";
import { useModal } from "../../hooks/useDialog";
import { SearchSelectIsVisible } from "../../stores/search/search";
import { BonusXThemeProvider } from "../../theme/BonusXThemeProvider";
import { EmptyComponent } from "../common/EmptyComponent";
import SearchModule from "../Header/SearchModule";
import HideOnScroll from "../HideOnScroll";
import { ProgressBar } from "../ProgressBar/ProgressBar";
import HeaderV2Actions from "./components/header-v2-actions/header-v2-actions";
import HeaderV2BackButton from "./components/header-v2-back-button";
import HeaderV2Logo from "./components/header-v2-logo";
import { HeaderV2TitleContainer } from "./components/header-v2-title-container";
import HeaderV2SearchModule from "./components/search-module/header-v2-search-module";
import useHeaderV2Store from "./hooks/use-header-v2-store.hook";
import { HeaderV2LayoutAction } from "./stores/header-v2-layout-store";
import { HeaderV2ProgressStore } from "./stores/header-v2-progress-store";
import type { IHeaderV2Store } from "./stores/header-v2-store";

function HeaderV2(headerProps?: IHeaderV2Store): JSX.Element {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const isSearchPanelVisible = useAtomValue(SearchSelectIsVisible);

  const {
    headerV2LayoutStore,
    headerV2ContentStore,
    setHeaderV2LayoutStore,
    setHeaderV2ContentStore,
    setHeaderV2ProgressStore,
  } = useHeaderV2Store();

  const headerV2ProgressStore = useAtomValue(HeaderV2ProgressStore);

  const { closeButton, showLogo, forceLogoExtended, actions, showLogoOnMobile, forceLogoCentered } =
    headerV2LayoutStore;
  const { title } = headerV2ContentStore;
  const { progressBar, progressBarPercentage } = headerV2ProgressStore;

  const { open: openModal } = useModal({
    children: closeButton?.modal,
  });

  useEffect(
    function setHeaderOptionsIfAvailable() {
      if (headerProps?.layoutStore) {
        setHeaderV2LayoutStore(headerProps.layoutStore);
      }

      if (headerProps?.contentStore) {
        setHeaderV2ContentStore(headerProps.contentStore);
      }

      if (headerProps?.progressStore) {
        setHeaderV2ProgressStore(headerProps.progressStore);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [headerProps]
  );

  const { performBackNavigation: performCloseNavigation } = useBackNavigation(
    closeButton?.url ? { url: closeButton.url, replace: closeButton.replace } : undefined
  );

  const onClickCloseButtonCallback = useCallback(
    function onClickCloseButton() {
      if (closeButton?.modal) {
        return openModal();
      }

      if (closeButton?.url) {
        return performCloseNavigation();
      }

      return navigate(-1);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate, closeButton, performCloseNavigation]
  );

  const titleStyles = isMobile ? typography.subtitle1 : typography.h6;

  const isSearchBarAvailable = actions?.indexOf(HeaderV2LayoutAction.search) !== -1;
  const isDesktopSearchBarVisible = !isMobile && isSearchBarAvailable;

  const hasAtLeastOneActionAvailable = actions && actions.length > 0;
  const isActionPanelAvailable = !isSearchPanelVisible && hasAtLeastOneActionAvailable;

  const isLogoClickable = headerProps?.isLogoClickable ?? true;

  const mobileHeaderBackButtonAndLogo = (
    <Box display={Displaying.flex} justifyContent={Alignment.start} alignItems={Alignment.center}>
      <HeaderV2BackButton />
      <HeaderV2Logo
        showLogo={showLogoOnMobile}
        forceLogoExtended={forceLogoExtended}
        isLogoClickable={isLogoClickable}
      />
    </Box>
  );

  const desktopHeaderBackButtonAndLogo = (
    <>
      {forceLogoCentered && <div />}
      <HeaderV2BackButton />
      <HeaderV2Logo showLogo={showLogo} forceLogoExtended={forceLogoExtended} isLogoClickable={isLogoClickable} />
      <div />
    </>
  );

  const selectedHeaderBackButtonAndLogo = isMobile ? mobileHeaderBackButtonAndLogo : desktopHeaderBackButtonAndLogo;

  return (
    <BonusXThemeProvider>
      <HideOnScroll>
        <Box sx={{ position: "sticky", top: 0, zIndex: 1100 }}>
          <AppBar position="static">
            {isSearchBarAvailable && isDesktopSearchBarVisible ? <HeaderV2SearchModule /> : <EmptyComponent />}

            {selectedHeaderBackButtonAndLogo}

            {title && (
              <HeaderV2TitleContainer isMobile={isMobile}>
                <Typography color="textPrimary" sx={{ ...titleStyles, overflow: "hidden", textOverflow: "ellipsis" }}>
                  {title}
                </Typography>
              </HeaderV2TitleContainer>
            )}

            {closeButton && (
              <IconButton id="header-close-button" onClick={onClickCloseButtonCallback}>
                <TimesSolid />
              </IconButton>
            )}

            {isActionPanelAvailable ? <HeaderV2Actions actions={actions} /> : <EmptyComponent />}
          </AppBar>
          {progressBar && <ProgressBar width={progressBarPercentage ?? 0} />}
        </Box>
      </HideOnScroll>

      {isSearchBarAvailable ? <SearchModule /> : <EmptyComponent />}
    </BonusXThemeProvider>
  );
}

export default HeaderV2;
